// COPIED from Remote-Leverage/hriq@3b50a234afdd packages/design-system/components/ui/select.tsx by apps/web/scripts/sync-design-system.mjs. Do not edit: change it in Hriq and re-sync.
// @ts-nocheck -- type-checked in Hriq under its own compiler options (the CRM's are stricter).
"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
  type RefObject,
} from "react";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  ViewportPortalLayer,
  type ViewportPortalPositionOptions,
} from "./viewport-portal-layer";

/* ─── Context ─────────────────────────────────────────────────────────────── */

type SelectContextValue = {
  open: boolean;
  setOpen: (v: boolean) => void;
  value: string;
  setValue: (v: string) => void;
  disabled?: boolean;
  triggerRef: RefObject<HTMLButtonElement | null>;
  labels: Map<string, string>;
  registerLabel: (value: string, label: string) => void;
  unregisterLabel: (value: string) => void;
  contentId: string;
  /**
   * When set, the closed trigger uses this for display — independent of SelectItem
   * mount order or portal/registry duplication. Prefer passing from option metadata.
   */
  getItemLabel?: (value: string) => string | undefined;
};

const SelectContext = createContext<SelectContextValue | null>(null);

function useSelectContext(component: string): SelectContextValue {
  const ctx = useContext(SelectContext);
  if (!ctx) throw new Error(`${component} must be used within <Select>`);
  return ctx;
}

/* ─── Root ─────────────────────────────────────────────────────────────────── */

function Select({
  value: valueProp,
  defaultValue,
  onValueChange,
  disabled,
  getItemLabel,
  children,
  ...props
}: {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  getItemLabel?: (value: string) => string | undefined;
  children: ReactNode;
} & Omit<ComponentProps<"div">, "children">) {
  const isControlled = valueProp !== undefined;
  const [internal, setInternal] = useState(defaultValue ?? "");
  const value = isControlled ? (valueProp as string) : internal;

  const setValue = useCallback(
    (next: string) => {
      if (!isControlled) setInternal(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [labels, setLabels] = useState(() => new Map<string, string>());
  const contentId = useId();

  const registerLabel = useCallback((v: string, label: string) => {
    setLabels((prev) => {
      if (prev.get(v) === label) return prev;
      const next = new Map(prev);
      next.set(v, label);
      return next;
    });
  }, []);

  const unregisterLabel = useCallback((v: string) => {
    setLabels((prev) => {
      if (!prev.has(v)) return prev;
      const next = new Map(prev);
      next.delete(v);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const ctx = useMemo(
    () =>
      ({
        open,
        setOpen,
        value,
        setValue,
        disabled,
        triggerRef,
        labels,
        registerLabel,
        unregisterLabel,
        contentId,
        getItemLabel,
      }) satisfies SelectContextValue,
    [
      open,
      value,
      setValue,
      disabled,
      labels,
      registerLabel,
      unregisterLabel,
      contentId,
      getItemLabel,
    ],
  );

  return (
    <SelectContext.Provider value={ctx}>
      <div data-slot="select" {...props}>
        {children}
      </div>
    </SelectContext.Provider>
  );
}

/* ─── Trigger / Value ─────────────────────────────────────────────────────── */

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: ComponentProps<"button"> & {
  size?: "sm" | "default";
}) {
  const ctx = useSelectContext("SelectTrigger");

  return (
    <button
      ref={ctx.triggerRef}
      type="button"
      data-slot="select-trigger"
      data-size={size}
      disabled={ctx.disabled}
      aria-expanded={ctx.open}
      aria-haspopup="listbox"
      aria-controls={ctx.open ? ctx.contentId : undefined}
      onClick={() => !ctx.disabled && ctx.setOpen(!ctx.open)}
      className={cn(
        "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground hover:bg-accent/50 dark:hover:bg-input/50 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow,background-color] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDownIcon className="size-4 opacity-50" />
    </button>
  );
}

/** Internal sentinel used by CustomSelect for empty value — never show literally */
const EMPTY_VALUE_SENTINEL = "__cs_empty__";

function SelectValue({ placeholder, className }: { placeholder?: string; className?: string }) {
  const ctx = useSelectContext("SelectValue");
  const v = ctx.value;
  const isEmpty = !v || v === EMPTY_VALUE_SENTINEL;
  const resolved = ctx.getItemLabel?.(v);
  const registered = ctx.labels.get(v);

  let show: string;
  if (ctx.getItemLabel) {
    // Resolver is authoritative: never show raw `value` when unknown (e.g. snake_case).
    show = resolved ?? registered ?? "";
  } else {
    show = registered ?? (isEmpty ? "" : v);
  }

  return (
    <span
      data-slot="select-value"
      className={cn(!show && "text-muted-foreground", className)}
    >
      {show || placeholder || ""}
    </span>
  );
}

/* ─── Content / Items ─────────────────────────────────────────────────────── */

function SelectContent({
  className,
  children,
  preferredMaxHeight = 320,
  /** When true, dropdown width matches the trigger (avoids narrow/wide jumps vs default min width). */
  matchTriggerWidth = false,
}: {
  className?: string;
  children?: ReactNode;
  preferredMaxHeight?: number;
  matchTriggerWidth?: boolean;
}) {
  const ctx = useSelectContext("SelectContent");
  const menuPosition = useMemo(
    (): ViewportPortalPositionOptions => ({
      variant: "menu",
      preferredMaxHeight,
      ...(matchTriggerWidth ? { matchReferenceWidth: true as const } : {}),
    }),
    [preferredMaxHeight, matchTriggerWidth],
  );

  /**
   * Fallback when `getItemLabel` is not passed: keep off-screen items mounted so
   * SelectItem registration can populate the trigger. When `getItemLabel` exists,
   * skip this (avoids duplicate mounts / unregister races and duplicate DOM).
   */
  const labelRegistry =
    !ctx.getItemLabel && !ctx.disabled ? (
      <div
        className={cn(
          "pointer-events-none fixed top-0 -left-[10000px] h-px w-px overflow-hidden opacity-0",
          ctx.open && "hidden",
        )}
        aria-hidden
      >
        <div
          id={ctx.open ? undefined : ctx.contentId}
          role="listbox"
          data-slot="select-content-registry"
          className="text-popover-foreground"
        >
          {children}
        </div>
      </div>
    ) : null;

  const portaled =
    ctx.open && !ctx.disabled ? (
      <ViewportPortalLayer
        open
        anchorRef={ctx.triggerRef}
        position={menuPosition}
        onClose={() => ctx.setOpen(false)}
        kind="menu"
        innerClassName={cn("p-1", className)}
        outsideIgnoreRefs={[ctx.triggerRef]}
      >
        <div
          id={ctx.contentId}
          role="listbox"
          data-slot="select-content"
          className="text-popover-foreground"
        >
          {children}
        </div>
      </ViewportPortalLayer>
    ) : null;

  return (
    <>
      {labelRegistry}
      {portaled}
    </>
  );
}

function labelFromChildren(children: ReactNode): string {
  if (typeof children === "string" || typeof children === "number") return String(children);
  return "";
}

function SelectItem({
  className,
  children,
  value,
  disabled,
  description,
  title,
}: {
  className?: string;
  children?: ReactNode;
  value: string;
  disabled?: boolean;
  description?: string;
  title?: string;
}) {
  const ctx = useSelectContext("SelectItem");
  const label = labelFromChildren(children);

  const { registerLabel, unregisterLabel } = ctx;
  // useEffect (not useLayoutEffect) keeps long lists from blocking interaction (INP) during open.
  useEffect(() => {
    registerLabel(value, label);
    return () => unregisterLabel(value);
  }, [value, label, registerLabel, unregisterLabel]);

  const selected = ctx.value === value;

  return (
    <div
      role="option"
      aria-selected={selected}
      data-slot="select-item"
      title={title}
      className={cn(
        "text-foreground relative flex w-full cursor-default select-none items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
      data-disabled={disabled ? "" : undefined}
      onMouseDown={(e) => {
        if (disabled) return;
        e.preventDefault();
        ctx.setValue(value);
        ctx.setOpen(false);
      }}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        {selected ? <CheckIcon className="size-4" /> : null}
      </span>
      <span className="flex flex-col gap-0.5">
        <span>{children}</span>
        {description ? (
          <span className="text-[10px] leading-tight text-muted-foreground font-normal">
            {description}
          </span>
        ) : null}
      </span>
    </div>
  );
}

function SelectGroup({ className, ...props }: ComponentProps<"div">) {
  return <div role="group" data-slot="select-group" className={className} {...props} />;
}

function SelectLabel({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="select-label"
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  );
}

function SelectSeparator({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="select-separator"
      className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="select-scroll-up-button"
      className={cn("hidden", className)}
      {...props}
    />
  );
}

function SelectScrollDownButton({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="select-scroll-down-button"
      className={cn("hidden", className)}
      {...props}
    />
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
